// PageCrumbs — the breadcrumb in the Studio's top bar (pen "Studio ·
// Brödsmulor", placed by pen Q76): every level above the page is a link
// back to it; the page itself ends the line. Short of room the middle levels shorten, then fold
// into "…" (studioCrumbs.ts); the full name is always on hover. Pure.

export { PageCrumbs };

import { useLingui } from "@lingui/react/macro";
import type { ReactNode } from "react";
import { Icon } from "../Icon/Icon.js";
import type { DeskCommand } from "../Studio/studioDesk.js";
import { fitCrumbs } from "./studioCrumbs.js";
import type { PageCrumb } from "./studioPages.js";

// Room for the line, in characters (the top bar at its narrowest).
const ROOM = 96;

const PageCrumbs = ({
    crumbs,
    here,
    onOpen,
}: {
    // The levels above the page, the outermost first.
    readonly crumbs: readonly PageCrumb[];
    // The page's own title (empty on the root page: only the root shows).
    readonly here: string | null;
    readonly onOpen: (commands: readonly DeskCommand[]) => void;
}): ReactNode => {
    const { t } = useLingui();
    const byId = new Map(crumbs.map((c, i) => [`${i}`, c]));
    const fitted = fitCrumbs(
        [
            ...crumbs.map((c, i) => ({ id: `${i}`, title: c.title })),
            ...(here === null ? [] : [{ id: "here", title: here }]),
        ],
        ROOM,
    );
    return (
        <nav className="bilda-pages__crumbs" aria-label={t`Breadcrumb`}>
            {fitted.map((step, i) => {
                const crumb = step.id === null ? undefined : byId.get(step.id);
                const last = i === fitted.length - 1;
                return (
                    <span
                        key={step.id ?? "folded"}
                        className="bilda-pages__crumb"
                    >
                        {i === 0 ? null : (
                            <Icon name="chevron-right" size="xs" />
                        )}
                        {crumb === undefined || last ? (
                            <span
                                className="bilda-pages__crumb-here"
                                aria-current={last ? "page" : undefined}
                                title={step.title}
                            >
                                {step.label}
                            </span>
                        ) : (
                            <button
                                type="button"
                                className="bilda-pages__crumb-link"
                                title={step.title}
                                onClick={() => onOpen(crumb.open)}
                            >
                                {step.label}
                            </button>
                        )}
                    </span>
                );
            })}
        </nav>
    );
};
